import {Injectable} from '@angular/core';

/**
 * Result returned by validateDocumentData() — the service validates the document
 * and returns errors so the component can display notifications and set vm flags.
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * TransactionValidationService
 *
 * Responsible for all business-rule validation across all transaction types
 * (AR Invoice, AP Invoice, Sales Order, Purchase Order, Quote, Shipment).
 *
 * Design principles:
 * - All methods accept data as parameters; no component state is read directly.
 * - UI side-effects (toast notifications, vm flag mutations) are NOT performed here.
 *   Methods return ValidationResult objects so the component can decide how to
 *   notify the user and update the view model.
 * - Date boundary checks (cut-off date, year-end date) remain in the component
 *   because they require vm.company lookup and notification calls — they will be
 *   extracted in Phase 8 (save pipeline) once the component is further decomposed.
 *
 * Phase 3 of the Invoice Form Refactor — see docs/Invoice Form Refactor.md
 */
@Injectable({
  providedIn: 'root'
})
export class TransactionValidationService {

  // ---------------------------------------------------------------------------
  // Field-level helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns true if the value is falsy (null, undefined, empty string, 0, false).
   * Used to check required form fields before save.
   *
   * Replaces: invoice-form.isFieldEmpty()
   */
  isFieldEmpty(value: any): boolean {
    return !value;
  }

  // ---------------------------------------------------------------------------
  // Document-level validation
  // ---------------------------------------------------------------------------

  /**
   * Validate the structural integrity of a transaction document.
   * Checks for required header fields, valid line items, and acceptable totals.
   * Returns a ValidationResult — the component is responsible for displaying
   * notifications and setting vm.isDocumentValid / vm.documentValidationErrors.
   *
   * Replaces: invoice-form.validateDocumentData()
   *
   * @param header         The document header object (vm.item.header)
   * @param detail         The document detail lines array (vm.item.detail)
   * @param status         The current document status string (vm.status)
   * @param balanceDue     The computed balance due (vm.balanceDue)
   * @param subTotal       The computed subtotal (vm.subTotal)
   * @param taxTotal       The computed tax total (vm.taxTotal)
   * @param grandTotal     The computed grand total (vm.grandTotal)
   * @param isCreditDoc    True if this is a credit invoice/memo (vm.isCreditInvoice or negative total)
   */
  validateDocumentData(
    header: any,
    detail: any[],
    status: string,
    balanceDue: number,
    subTotal: number,
    taxTotal: number,
    grandTotal: number,
    isCreditDoc: boolean
  ): ValidationResult {
    const errors: string[] = [];

    // Check for empty or missing document lines
    if (!detail || detail.length === 0) {
      errors.push('Document has no line items');
    }

    // Validate balance due
    if (balanceDue < 0) {
      errors.push('Invalid balance due calculation');
    }

    // Validate entity information
    if (!header?.entity_id) {
      errors.push('Missing entity information');
    }

    // Validate document number (only required for non-new documents)
    if ((!header?.number || String(header.number).trim() === '') && status?.toUpperCase() !== 'NEW') {
      errors.push('Invalid or missing document number');
    }

    // Validate document date
    if (!header?.header_date || isNaN(new Date(header.header_date).getTime())) {
      errors.push('Invalid document date');
    }

    // Validate line items
    (detail || []).forEach((line: any, index: number) => {
      if (line.control_line === false) {
        // For new lines (non-UUID id), compute debit_credit before validating it
        const lineId = line.id;
        const isApiLine = lineId && typeof lineId === 'string' && lineId.includes('-');
        if (!isApiLine && line.amount != null && line.quantity != null) {
          line.debit_credit = line.amount * line.quantity;
        }

        // Quantity rules — only validate lines that have some data entered
        const q = line.quantity;
        const hasQty = !(q === null || q === undefined);
        const hasAmt = line.amount != null && Number(line.amount) !== 0;
        const isBlankRow = !hasAmt && (Number(q ?? 0) === 0);
        if (!isBlankRow) {
          if (!hasQty) {
            errors.push(`Missing quantity for line item ${index + 1}`);
          } else if (Number(q) === 0 && !hasAmt) {
            errors.push(`Invalid quantity for line item ${index + 1}`);
          }

          if (line.amount === undefined || line.amount === null) {
            errors.push(`Invalid unit price for line item ${index + 1}`);
          }
        }

        // Only flag invalid ledger post value if the line has actual data entered
        const hasData = Number(line.quantity ?? 0) !== 0 || Number(line.amount ?? 0) !== 0;
        if (hasData && !line.debit_credit) {
          errors.push(`Invalid ledger post value line item ${index + 1}`);
        }
      }
    });

    // Validate totals — credit invoices/memos are allowed to have negative totals
    if (!isCreditDoc) {
      if (subTotal < 0) {
        errors.push('Negative subtotal is not allowed');
      }
      if (taxTotal < 0) {
        errors.push('Negative tax total is not allowed');
      }
      if (grandTotal < 0) {
        errors.push('Negative grand total is not allowed');
      }
    }

    // Validate document status
    const validStatuses = ['Draft', 'Pending', 'Pending Approval', 'Approved', 'Rejected', 'Unknown', 'Cancelled', 'New', 'Editing', 'Posted', 'new', 'posted', 'draft'];
    if (!validStatuses.includes(status)) {
      errors.push('Invalid document status ' + status);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // ---------------------------------------------------------------------------
  // Pre-save detail validation
  // ---------------------------------------------------------------------------

  /**
   * Validate detail lines before save — checks for negative amounts on item lines,
   * missing descriptions, and missing accounts (for AR/AP sub-ledgers).
   * Returns errors for the component to display; does not mutate vm state.
   *
   * This covers the detail-loop portion of validateDocumentBeforeSave() that is
   * pure rule-checking and has no dependency on component services.
   *
   * @param detail     The document detail lines array (vm.item.detail)
   * @param subLedger  The sub-ledger key ('ar' | 'ap' | 'so' | 'po' | 'quote' | 'ss' | 'ps')
   */
  validateDetailLines(detail: any[], subLedger: string): string[] {
    const errors: string[] = [];
    let detailFound = false;

    for (const [x, value] of Object.entries(detail || [])) {
      const line = detail[x as any];

      // Recompute debit_credit for new (non-UUID) lines
      const lineId = line.id;
      const isApiLine = lineId && typeof lineId === 'string' && lineId.includes('-');
      if (!isApiLine) {
        line.debit_credit = line.amount * line.quantity;
      }

      // Negative amounts on item lines are not allowed — use negative quantity instead
      if (line.amount < 0 && line.item_id) {
        errors.push('Amounts cannot be negative on lines with items. Please use a negative quantity.');
      }

      if (!line.debit_credit) {
        line.debit_credit = 0;
      }

      if (line.debit_credit) {
        detailFound = true;

        if (!line.description) {
          errors.push('Detail description is required');
        }

        if (!line.account_id && (subLedger === 'ap' || subLedger === 'ar')) {
          errors.push('Detail account is required');
        }
      }
    }

    if (!detailFound) {
      errors.push('At least one non-zero detail row is required');
    }

    return errors;
  }

  // ---------------------------------------------------------------------------
  // Payment validation
  // ---------------------------------------------------------------------------

  /**
   * Validate payment fields when a payment amount has been entered.
   * Returns errors for the component to display.
   *
   * @param paymentAmount    The entered payment amount (vm.paymentAmount)
   * @param paymentHeader    The payment header object (vm.payment.header)
   */
  validatePaymentFields(paymentAmount: number | null, paymentHeader: any): string[] {
    const errors: string[] = [];

    if (!paymentAmount) {
      return errors;
    }

    if (!paymentHeader?.bank_id) {
      errors.push('You have entered a payment amount, a Bank Account is required');
    }

    if (!paymentHeader?.payment_type_id) {
      errors.push('You have entered a payment amount, a Payment Type is required');
    }

    if (!paymentHeader?.header_date) {
      errors.push('You have entered a payment amount, a Date is required');
    }

    return errors;
  }
}
